import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  listBottlePhotos,
  moderateBottlePhoto,
  deleteBottlePhoto,
  type BottlePhoto,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";

export default function AdminBottlePhotos() {
  const [photos, setPhotos] = useState<BottlePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "featured" | "rejected" | "all">("pending");
  const [searchQuery, setSearchQuery] = useState("");

  // Zoom Modal
  const [zoomedPhoto, setZoomedPhoto] = useState<BottlePhoto | null>(null);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes zoom modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && zoomedPhoto) setZoomedPhoto(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomedPhoto]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listBottlePhotos(statusFilter);
      setPhotos(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load bottle photos", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const filteredPhotos = useMemo(() => {
    if (!searchQuery.trim()) return photos;
    const q = searchQuery.toLowerCase().trim();
    return photos.filter(
      (p) =>
        (p.bottle_name && p.bottle_name.toLowerCase().includes(q)) ||
        (p.distillery_name && p.distillery_name.toLowerCase().includes(q)) ||
        (p.submitted_by_name && p.submitted_by_name.toLowerCase().includes(q)) ||
        (p.caption && p.caption.toLowerCase().includes(q))
    );
  }, [photos, searchQuery]);

  const handleModerate = async (
    photo: BottlePhoto,
    status: "approved" | "rejected" | "featured",
    setPrimary = false
  ) => {
    try {
      await moderateBottlePhoto(photo.id, status, setPrimary, photo.bottle_id, photo.photo_url);
      showToast(`✓ Marked photo as ${status}${setPrimary ? " and set as bottle primary" : ""}`);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to update photo", "err");
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!window.confirm("Are you sure you want to permanently delete this photo record?")) return;
    try {
      await deleteBottlePhoto(photoId);
      showToast("✓ Deleted photo record");
      if (zoomedPhoto?.id === photoId) setZoomedPhoto(null);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to delete photo", "err");
    }
  };

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="photos" badgeCounts={{ photos: photos.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Bottle Photos &amp; Submissions</h1>
          <p style={styles.subtitle}>
            Moderate member-submitted photos, approve bottle photography, and feature high-res bar shots.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {(["pending", "approved", "featured", "rejected", "all"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              style={statusFilter === tab ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => setStatusFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by bottle, distillery, submitter…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div style={styles.loader}>Loading bottle photos…</div>
      ) : filteredPhotos.length === 0 ? (
        <div style={styles.emptyState}>No bottle photos found in this queue.</div>
      ) : (
        <div style={styles.photosGrid}>
          {filteredPhotos.map((p) => (
            <div key={p.id} style={styles.card}>
              <div style={styles.imageWrap} onClick={() => setZoomedPhoto(p)}>
                <img src={p.photo_url} alt="" style={styles.thumbImg} loading="lazy" />
                <span style={styles.zoomHint}>🔍 Click to Zoom</span>
              </div>

              <div style={styles.cardBody}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={styles.bottleTitle}>{p.bottle_name}</h3>
                    {p.distillery_name && (
                      <div style={styles.distilleryName}>{p.distillery_name}</div>
                    )}
                  </div>
                  <span
                    style={{
                      ...styles.statusBadge,
                      background:
                        p.status === "approved" || p.status === "featured"
                          ? "rgba(39,174,96,0.15)"
                          : p.status === "rejected"
                          ? "rgba(224,80,64,0.15)"
                          : "rgba(249,175,54,0.15)",
                      color:
                        p.status === "approved" || p.status === "featured"
                          ? "#27ae60"
                          : p.status === "rejected"
                          ? "#E05040"
                          : "var(--gold)",
                    }}
                  >
                    {(p.status || "unknown").toUpperCase()}
                  </span>
                </div>

                {p.caption && <div style={styles.captionBox}>“{p.caption}”</div>}

                <div style={styles.submitterMeta}>
                  <span>By: <strong>{p.submitted_by_name || "Guest / Member"}</strong></span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>

                {/* Library's approve/reject endpoints only accept PENDING
                    submissions — approved photos are a read-only gallery. */}
                <div style={styles.actionRow}>
                  {p.status === "pending" && (
                    <button
                      type="button"
                      style={styles.btnApprove}
                      onClick={() => handleModerate(p, "approved", true)}
                    >
                      ✓ Approve &amp; Set Primary
                    </button>
                  )}
                  {p.status === "pending" && (
                    <button
                      type="button"
                      style={styles.btnFeature}
                      onClick={() => handleModerate(p, "featured")}
                    >
                      ★ Approve
                    </button>
                  )}
                  {p.status === "pending" && (
                    <button
                      type="button"
                      style={styles.btnReject}
                      onClick={() => handleModerate(p, "rejected")}
                    >
                      Reject
                    </button>
                  )}
                  {p.status === "pending" && (
                    <button
                      type="button"
                      style={styles.btnDelete}
                      onClick={() => handleDelete(p.id)}
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedPhoto && (
        <div style={styles.modalOverlay} onClick={() => setZoomedPhoto(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{zoomedPhoto.bottle_name}</h2>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  Submitted by {zoomedPhoto.submitted_by_name || "Guest"} on{" "}
                  {new Date(zoomedPhoto.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setZoomedPhoto(null)}
              >
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <img
                src={zoomedPhoto.photo_url}
                alt=""
                style={styles.fullImg}
              />
              {zoomedPhoto.caption && (
                <div style={styles.modalCaption}>“{zoomedPhoto.caption}”</div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.btnApprove}
                onClick={() => {
                  handleModerate(zoomedPhoto, "approved", true);
                  setZoomedPhoto(null);
                }}
              >
                Approve &amp; Set Bottle Primary
              </button>
              <button
                type="button"
                style={styles.btnFeature}
                onClick={() => {
                  handleModerate(zoomedPhoto, "featured");
                  setZoomedPhoto(null);
                }}
              >
                ★ Feature in Gallery
              </button>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={() => setZoomedPhoto(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  filterTabs: { display: "flex", flexWrap: "wrap", gap: 6 },
  tabBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  tabBtnActive: { background: "rgba(249,175,54,0.15)", border: "1px solid var(--gold)", borderRadius: 8, color: "var(--gold)", padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  searchInput: { minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  photosGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" },
  imageWrap: { position: "relative", width: "100%", height: 220, background: "var(--bg)", cursor: "zoom-in", overflow: "hidden" },
  thumbImg: { width: "100%", height: "100%", objectFit: "contain" },
  zoomHint: { position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#FFF", fontSize: 10, padding: "2px 6px", borderRadius: 4 },
  // anywhere + minWidth 0: long bottle / distillery / submitter names were cut off by the card.
  cardBody: { padding: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 0, overflowWrap: "anywhere" },
  bottleTitle: { fontSize: 15, fontWeight: 700, margin: 0, color: "var(--text)" },
  distilleryName: { fontSize: 11, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, flexShrink: 0, whiteSpace: "nowrap" },
  captionBox: { fontSize: 12, color: "var(--muted)", fontStyle: "italic", background: "rgba(0,0,0,0.2)", padding: "6px 8px", borderRadius: 4, borderLeft: "2px solid var(--border)" },
  submitterMeta: { display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--muted)" },
  actionRow: { display: "flex", gap: 6, alignItems: "center", marginTop: "auto", paddingTop: 8, borderTop: "1px solid var(--border)" },
  btnApprove: { background: "rgba(39,174,96,0.15)", border: "1px solid rgba(39,174,96,0.4)", color: "#27ae60", padding: "6px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", flex: 1 },
  btnFeature: { background: "rgba(249,175,54,0.15)", border: "1px solid rgba(249,175,54,0.4)", color: "var(--gold)", padding: "6px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" },
  btnReject: { background: "rgba(224,80,64,0.1)", border: "1px solid rgba(224,80,64,0.3)", color: "#E05040", padding: "6px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" },
  btnDelete: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 16, cursor: "pointer", padding: "0 4px" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalBody: { padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 12, overflowY: "auto" },
  fullImg: { maxWidth: "100%", maxHeight: "55vh", objectFit: "contain", borderRadius: 8, background: "var(--bg)" },
  modalCaption: { fontSize: 13, color: "var(--text)", fontStyle: "italic", textAlign: "center" },
  modalFooter: { display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 20px", borderTop: "1px solid var(--border)" },
};
